import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X, User, ShoppingBag, Send, Users, Phone, Store } from 'lucide-react';
import { Menu } from '@headlessui/react';
import { useAuth } from '../context';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;
  const { authState } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActivePath = (path) => {
    return currentPath === path ? 'text-green-600' : 'text-gray-600 hover:text-green-600';
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
    setIsMenuOpen(false);
  };

  const ProductDropdown = () => (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className={`${isActivePath('/product')} transition-colors duration-200 text-sm font-medium`}>
          Product
        </Menu.Button>
      </div>
      <div className="fixed left-0 w-full transform z-50">
        <Menu.Items className="mt-2 w-full bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-8 py-6 grid grid-cols-3 gap-8">
            <div className="col-span-1 pr-8 border-r border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Products</h3>
              <p className="text-gray-600">Discover innovative tools that empower your financial and shopping needs.</p>
            </div>
            
            <div className="col-span-1">
              <h4 className="text-lg font-medium text-gray-900 mb-6">Marketplace</h4>
              <div className="space-y-6">
                {[
                  { title: 'Vouchers', desc: 'Send value using digital vouchers', icon: ShoppingBag, path: '/marketplace' },
                  { title: 'Categories', desc: 'Discover services for your daily needs.', icon: Store, path: '/marketplace' },
                  // { title: 'How It Works', desc: 'Discover how it works', icon: Users, path: '/how-it-works' }
                ].map((item, index) => (
                  <Link to={item.path} key={index} className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">{item.title}</h5>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="col-span-1">
              <h4 className="text-lg font-medium text-gray-900 mb-6">Send Money</h4>
              <div className="space-y-6">
                {[
                  { title: 'Stablecoin', desc: 'Understanding USDC', icon: Send, path: '/send-money' },
                  { title: 'Countries', desc: 'Countries you can send money to', icon: Users, path: '/marketplace' },
                  // { title: 'How It works', desc: 'Discover how it works', icon: Store, path: '/how-it-works' }
                ].map((item, index) => (
                  <Link to={item.path} key={index} className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">{item.title}</h5>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Menu.Items>
      </div>
    </Menu>
  );

  const CompanyDropdown = () => (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className={`${isActivePath('/company')} transition-colors duration-200 text-sm font-medium`}>
          Company
        </Menu.Button>
      </div>
      <div className="fixed left-0 w-full transform z-50">
        <Menu.Items className="mt-2 w-full bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-8 py-6 grid grid-cols-3 gap-8">
            <div className="col-span-1 pr-8 border-r border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Company</h3>
              <p className="text-gray-600">Learn more about our mission, values, and the team driving our success.</p>
            </div>
            
            <div className="col-span-1">
              <h4 className="text-lg font-medium text-gray-900 mb-6">About Us</h4>
              <div className="space-y-6">
                {[
                  { title: 'Our Mission', desc: 'Who we are and the vision that guides us.', icon: Users, path: '/about-us' },
                  { title: 'Blogs', desc: 'Insights and stories from the world of Lipaworld.', icon: Store, path: '/blog' }
                ].map((item, index) => (
                  <Link to={item.path} key={index} className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">{item.title}</h5>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="col-span-1">
              <h4 className="text-lg font-medium text-gray-900 mb-6">Connect</h4>
              <div 
                onClick={scrollToBottom} 
                className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 p-2 rounded-lg"
              >
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">Socials</h5>
                  <p className="text-sm text-gray-600">Connect with us online.</p>
                </div>
              </div>
            </div>
          </div>
        </Menu.Items>
      </div>
    </Menu>
  );

  const menuItems = [
    { component: ProductDropdown, order: 1 },
    { component: CompanyDropdown, order: 2 },
    { path: '/partners', label: 'Partners', order: 4 },
    { path: '/contact-us', label: 'Contact Us', order: 5 },
    { path: '/marketplace', label: 'Shop', order: 3 },
  ];

  // Mobile menu items (no dropdowns)
  const mobileMenuItems = [
    { path: '/', label: 'Home', order: 1 },
    { path: '/about-us', label: 'About Us', order: 2 },
    { path: '/send-money', label: 'Send Money', order: 3 },
    { path: '/blog', label: 'Blog', order: 4 },
    { path: '/partners', label: 'Partners', order: 5 },
    { path: '/contact-us', label: 'Contact Us', order: 6 },
    { path: '/marketplace', label: 'Shop', order: 7 },
  ];

  const sortedMenuItems = [...menuItems].sort((a, b) => a.order - b.order);
  const sortedMobileMenuItems = [...mobileMenuItems].sort((a, b) => a.order - b.order);

  const AuthButtons = () => {
    if (authState.isAuthenticated) {
      return (
        <Link to="/account-overview" className="flex items-center space-x-2 text-gray-600 hover:text-green-600">
          <User className="h-5 w-5" />
          <span className="text-sm font-medium">Profile</span>
        </Link>
      );
    }

    return (
      <>
        <Link to="/sign-in" className="text-gray-600 hover:text-green-600 text-sm font-medium">
          Log In
        </Link>
        <Link
          to="/sign-up"
          className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors duration-200"
        >
          Register
        </Link>
      </>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <Link to="/" className="text-2xl font-bold text-green-600">
              <img src="/img/header/logo-light.png" alt="Lipaworld" className="h-8" />
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            {sortedMenuItems.map((item, index) => (
              item.component ? (
                <item.component key={index} />
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${isActivePath(item.path)} transition-colors duration-200 text-sm font-medium`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <AuthButtons />
          </div>

          <div className="flex md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-green-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
            >
              {isMenuOpen ? <X className="block h-6 w-6" /> : <MenuIcon className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - Updated with direct links instead of dropdowns */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="fixed inset-y-0 right-0 max-w-xs w-full bg-white shadow-xl">
            <div className="flex justify-end p-4">
              <button
                onClick={toggleMenu}
                className="text-gray-600 hover:text-green-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="px-4 py-2">
              {sortedMobileMenuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block py-2 text-base font-medium ${isActivePath(item.path)}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-gray-200 mt-4 pt-4">
                <AuthButtons />
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;